/**
 * Typed client for the tilemap API (_docs/tilemap/03 §3). Separate from the
 * graph apiClient because the chunk-autosave flush needs to distinguish a 409
 * (stale version → refetch/merge) from other errors, which the graph client's
 * throw-on-any-error helper collapses.
 */

import type { TilemapDocument, Viewport } from '../model/types';

const BASE_URL = '/api';

export interface ExportJob {
    uuid: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    path?: string | null;
    download_url?: string;
    warnings?: string[];
    error?: string | null;
}

export interface ImportJob {
    uuid: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    report?: {
        warnings?: string[];
        plan?: {
            mode: 'new' | 'update';
            target_tilemap_uuid?: string | null;
            tilemap_name: string;
            layers: Array<{ name: string; chunk_count: number; matches_existing_layer: boolean }>;
        };
        tilemap_uuid?: string | null;
        errors?: string[];
    } | null;
    error?: string | null;
}

/** A chunk edit in a flush: base64 data, or null to delete the chunk. */
export interface ChunkFlush {
    layer: string;
    cx: number;
    cy: number;
    data: string | null;
}

export interface SaveChunksResult {
    ok: boolean;
    version?: number; // present on success
    conflictVersion?: number; // present on 409
}

async function json<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await fetch(`${BASE_URL}${path}`, {
        method,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `API error: ${res.status}`);
    }
    if (res.status === 204) return {} as T;
    return res.json();
}

export const tilemapClient = {
    // ── Catalog ──────────────────────────────────────────────────────────────
    listTilemaps(projectUuid: string) {
        return json<
            Array<{
                uuid: string;
                name: string;
                cell_size: number;
                version: number;
                layers_count: number;
                tiles_painted: number;
                thumbnail_uuid: string | null;
                updated_at: string;
            }>
        >('GET', `/projects/${projectUuid}/tilemaps`);
    },

    listTilesets(projectUuid: string) {
        return json<
            Array<{
                uuid: string;
                name: string;
                tile_width: number;
                tile_height: number;
                columns: number | null;
                tile_count: number | null;
                layers_count: number;
                texture_asset?: { uuid: string };
            }>
        >('GET', `/projects/${projectUuid}/tilesets`);
    },

    // ── Documents ────────────────────────────────────────────────────────────
    createTilemap(projectUuid: string, data: { name: string; cell_size?: number; tileset_uuid?: string }) {
        return json<TilemapDocument>('POST', `/projects/${projectUuid}/tilemaps`, data);
    },

    getTilemap(projectUuid: string, tilemapUuid: string) {
        return json<TilemapDocument>('GET', `/projects/${projectUuid}/tilemaps/${tilemapUuid}`);
    },

    getChunks(projectUuid: string, tilemapUuid: string, layerUuid: string, bbox?: [number, number, number, number]) {
        const q = new URLSearchParams({ layer: layerUuid });
        if (bbox) q.set('bbox', bbox.join(','));
        return json<{ layer: string; chunks: Array<{ cx: number; cy: number; data: string }> }>(
            'GET',
            `/projects/${projectUuid}/tilemaps/${tilemapUuid}/chunks?${q.toString()}`,
        );
    },

    /**
     * Flush dirty chunks under optimistic locking. Resolves with the outcome
     * rather than throwing on 409 (which is an expected, recoverable state).
     */
    async saveChunks(
        projectUuid: string,
        tilemapUuid: string,
        baseVersion: number,
        chunks: ChunkFlush[],
        meta?: { viewport?: Viewport | null; settings?: Record<string, unknown> | null },
    ): Promise<SaveChunksResult> {
        const res = await fetch(`${BASE_URL}/projects/${projectUuid}/tilemaps/${tilemapUuid}/chunks`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ base_version: baseVersion, chunks, meta }),
        });

        if (res.status === 409) {
            const body = await res.json().catch(() => ({}));
            return { ok: false, conflictVersion: body.current_version };
        }
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.message || `API error: ${res.status}`);
        }
        const body = await res.json();
        return { ok: true, version: body.version };
    },

    updateTilemap(projectUuid: string, tilemapUuid: string, data: Record<string, unknown>) {
        return json<unknown>('PATCH', `/projects/${projectUuid}/tilemaps/${tilemapUuid}`, data);
    },

    /** Delete a map. `force` deletes even when referenced by nodes (they placeholder). */
    deleteTilemap(projectUuid: string, tilemapUuid: string, force = false) {
        const q = force ? '?force=1' : '';
        return json<unknown>('DELETE', `/projects/${projectUuid}/tilemaps/${tilemapUuid}${q}`);
    },

    // ── Engine interchange (_docs/tilemap/06) ───────────────────────────────

    /** Kick off a JoystickEngine/JSON export job; poll via getExportJob. */
    exportTilemap(projectUuid: string, tilemapUuid: string, format: 'joystick' | 'json') {
        return json<ExportJob>('POST', `/projects/${projectUuid}/tilemaps/${tilemapUuid}/export`, { format });
    },

    getExportJob(exportUuid: string) {
        return json<ExportJob>('GET', `/exports/${exportUuid}`);
    },

    /** Upload an engine zip or Tiled zip for import; poll via getImportJob. */
    async importTilemapZip(projectUuid: string, file: File, dryRun = false): Promise<ImportJob> {
        const form = new FormData();
        form.append('file', file);
        if (dryRun) form.append('dry_run', '1');
        const res = await fetch(`${BASE_URL}/projects/${projectUuid}/tilemaps/import`, { method: 'POST', body: form });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.message || `Import failed: ${res.status}`);
        }
        return res.json();
    },

    getImportJob(importUuid: string) {
        return json<ImportJob>('GET', `/imports/${importUuid}`);
    },

    // ── Tilesets ─────────────────────────────────────────────────────────────
    createTileset(
        projectUuid: string,
        data: { name: string; texture_asset_uuid: string; tile_width: number; tile_height: number; spacing?: number; margin?: number },
    ) {
        return json<{ uuid: string; name: string; columns: number | null; tile_count: number | null }>(
            'POST',
            `/projects/${projectUuid}/tilesets`,
            data,
        );
    },

    /** Partial-merge per-tile metadata. `{ "12": {solid:true}, "13": null }` (null deletes). */
    updateTileMeta(projectUuid: string, tilesetUuid: string, meta: Record<string, unknown>) {
        return json<{ tile_meta: Record<string, unknown> | null }>(
            'PATCH',
            `/projects/${projectUuid}/tilesets/${tilesetUuid}/tile-meta`,
            { meta },
        );
    },

    /** Full replace of a tileset's auto-tiling rule table (_docs/tilemap/07 roadmap). */
    updateAutotileRules(projectUuid: string, tilesetUuid: string, rules: Record<string, unknown>) {
        return json<{ autotile_rules: Record<string, unknown> | null }>(
            'PUT',
            `/projects/${projectUuid}/tilesets/${tilesetUuid}/autotile-rules`,
            { rules },
        );
    },

    // ── Layers ───────────────────────────────────────────────────────────────
    createLayer(projectUuid: string, tilemapUuid: string, data: { name: string; tileset_uuid: string; above?: string }) {
        return json<{ uuid: string; name: string; sort_order: number }>(
            'POST',
            `/projects/${projectUuid}/tilemaps/${tilemapUuid}/layers`,
            data,
        );
    },

    updateLayer(layerUuid: string, data: Record<string, unknown>) {
        return json<unknown>('PATCH', `/tilemap-layers/${layerUuid}`, data);
    },

    reorderLayers(projectUuid: string, tilemapUuid: string, orderedUuids: string[]) {
        return json<unknown>('POST', `/projects/${projectUuid}/tilemaps/${tilemapUuid}/layers/reorder`, {
            ordered_uuids: orderedUuids,
        });
    },

    deleteLayer(layerUuid: string) {
        return json<unknown>('DELETE', `/tilemap-layers/${layerUuid}`);
    },

    /** Multipart image upload → asset (reuses the graph asset pipeline). */
    async uploadImage(projectUuid: string, file: File): Promise<{ uuid: string; name: string }> {
        const form = new FormData();
        form.append('file', file);
        const res = await fetch(`${BASE_URL}/projects/${projectUuid}/assets`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: form,
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.message || `Upload failed: ${res.status}`);
        }
        return res.json();
    },
};
